import { useState, useEffect } from 'react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../lib/api';

export default function Approvals() {
  const { user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [rejectReason, setRejectReason] = useState('');
  const [processing, setProcessing] = useState(null);
  const [filter, setFilter] = useState('pending');

  useEffect(() => { loadOrders(); }, []);

  async function loadOrders() {
    setLoading(true);
    try {
      const res = await api.getPendingOrders();
      setOrders(res.orders || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  async function handleApprove(orderId) {
    setProcessing(orderId);
    setError('');
    try {
      await api.approveOrder(orderId);
      await loadOrders();
    } catch (e) {
      setError(e.message);
    } finally {
      setProcessing(null);
    }
  }

  async function handleReject(orderId) {
    setProcessing(orderId);
    setError('');
    try {
      await api.rejectOrder(orderId, rejectReason);
      setRejectReason('');
      setExpandedId(null);
      await loadOrders();
    } catch (e) {
      setError(e.message);
    } finally {
      setProcessing(null);
    }
  }

  // Employees see the orders they sent for approval, without the
  // approve/reject controls.
  const isAdmin = user?.role === 'customer_admin';
  const filtered = orders.filter(o => filter === 'all' || o.status === filter);
  const pendingCount = orders.filter(o => o.status === 'pending').length;

  return (
    <div>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>{isAdmin ? 'Order Approvals' : 'Pending Orders'}</h1>
          <p style={s.subtitle}>
            {isAdmin
              ? (pendingCount > 0 ? `${pendingCount} order${pendingCount !== 1 ? 's' : ''} pending approval` : 'No orders pending approval')
              : (pendingCount > 0 ? `${pendingCount} order${pendingCount !== 1 ? 's' : ''} you sent ${pendingCount !== 1 ? 'are' : 'is'} waiting for approval` : 'Nothing you sent is waiting for approval')}
          </p>
        </div>
        <div style={s.filterGroup}>
          {['pending', 'approved', 'rejected', 'all'].map(f => (
            <button key={f} style={filter === f ? s.filterActive : s.filterBtn} onClick={() => setFilter(f)}>
              {f.charAt(0).toUpperCase() + f.slice(1)}
            </button>
          ))}
        </div>
      </div>

      {error && <div style={s.error}>{error}</div>}

      {loading ? (
        <div style={s.empty}>Loading orders...</div>
      ) : filtered.length === 0 ? (
        <div style={s.empty}>No {filter === 'all' ? '' : filter} orders found.</div>
      ) : (
        <div style={s.list}>
          {filtered.map(order => (
            <div key={order.orderId} style={s.card}>
              <div style={s.cardHeader} onClick={() => setExpandedId(expandedId === order.orderId ? null : order.orderId)}>
                <div style={s.cardLeft}>
                  <span style={s.cardSubmitter}>{order.submittedByName || order.submittedBy}</span>
                  <span style={s.cardDate}>
                    {new Date(order.submittedAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                </div>
                <div style={s.cardRight}>
                  <span style={s.cardTotal}>${order.total?.toFixed(2)}</span>
                  <span style={['pending', 'approving'].includes(order.status) ? s.statusPending : order.status === 'approved' ? s.statusApproved : s.statusRejected}>
                    {order.status === 'approving' ? 'being approved' : order.status}
                  </span>
                  <span style={s.expand}>{expandedId === order.orderId ? '−' : '+'}</span>
                </div>
              </div>

              {expandedId === order.orderId && (
                <div style={s.cardBody}>
                  {order.deliveryMethod && (
                    <p style={s.meta}>Delivery: <strong>{order.deliveryMethod}</strong></p>
                  )}
                  {order.poNumber && <p style={s.meta}>PO: <strong>{order.poNumber}</strong></p>}
                  {order.notes && <p style={s.meta}>Notes: {order.notes}</p>}

                  <table style={s.table}>
                    <thead>
                      <tr>
                        <th style={s.th}>Item</th>
                        <th style={{ ...s.th, textAlign: 'center' }}>Qty</th>
                        <th style={{ ...s.th, textAlign: 'right' }}>Price</th>
                        <th style={{ ...s.th, textAlign: 'right' }}>Total</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(order.lines || []).map((line, i) => (
                        <tr key={i}>
                          <td style={s.td}>{line.name || line.sku || `Item ${line.itemId}`}</td>
                          <td style={{ ...s.td, textAlign: 'center' }}>{line.quantity}</td>
                          <td style={{ ...s.td, textAlign: 'right' }}>${(line.price || 0).toFixed(2)}</td>
                          <td style={{ ...s.td, textAlign: 'right' }}>${((line.quantity || 0) * (line.price || 0)).toFixed(2)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>

                  {isAdmin && order.status === 'pending' && (
                    <div style={s.actions}>
                      <button
                        style={s.approveBtn}
                        onClick={() => handleApprove(order.orderId)}
                        disabled={processing === order.orderId}
                      >
                        {processing === order.orderId ? 'Processing...' : 'Approve Order'}
                      </button>
                      <div style={s.rejectGroup}>
                        <input
                          style={s.rejectInput}
                          placeholder="Rejection reason (optional)"
                          value={rejectReason}
                          onChange={e => setRejectReason(e.target.value)}
                        />
                        <button
                          style={s.rejectBtn}
                          onClick={() => handleReject(order.orderId)}
                          disabled={processing === order.orderId}
                        >
                          Reject
                        </button>
                      </div>
                    </div>
                  )}

                  {order.status !== 'pending' && order.reviewedBy && (
                    <p style={s.meta}>
                      {order.status === 'approved' ? 'Approved' : 'Rejected'} by {order.reviewedBy}
                      {order.reviewedAt && ` on ${new Date(order.reviewedAt).toLocaleDateString()}`}
                      {order.rejectionReason && ` — "${order.rejectionReason}"`}
                    </p>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const s = {
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.5rem', flexWrap: 'wrap', gap: '1rem' },
  title: { fontSize: '1.4rem', fontWeight: 700, color: '#333', margin: 0 },
  subtitle: { fontSize: '0.85rem', color: 'rgba(0,0,0,0.45)', margin: '0.25rem 0 0' },
  filterGroup: { display: 'flex', gap: 2, background: '#f5f5f5', borderRadius: 8, padding: 3 },
  filterBtn: { background: 'transparent', border: 'none', borderRadius: 6, padding: '0.4rem 0.8rem', fontSize: '0.8rem', color: 'rgba(0,0,0,0.4)', cursor: 'pointer', fontWeight: 500 },
  filterActive: { background: '#fff', border: 'none', borderRadius: 6, padding: '0.4rem 0.8rem', fontSize: '0.8rem', color: '#A50B18', cursor: 'pointer', fontWeight: 600, boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
  error: { background: 'rgba(231,76,60,0.06)', border: '1px solid rgba(231,76,60,0.2)', borderRadius: 8, padding: '0.75rem 1rem', color: '#c0392b', fontSize: '0.85rem', marginBottom: '1rem' },
  empty: { textAlign: 'center', color: 'rgba(0,0,0,0.4)', padding: '3rem', fontSize: '0.9rem' },
  list: { display: 'flex', flexDirection: 'column', gap: '0.75rem' },
  card: { background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10, overflow: 'hidden' },
  cardHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '1rem 1.25rem', cursor: 'pointer' },
  cardLeft: { display: 'flex', flexDirection: 'column', gap: 2 },
  cardSubmitter: { fontWeight: 600, fontSize: '0.9rem', color: '#333' },
  cardDate: { fontSize: '0.78rem', color: 'rgba(0,0,0,0.4)' },
  cardRight: { display: 'flex', alignItems: 'center', gap: '0.75rem' },
  cardTotal: { fontWeight: 700, color: '#333' },
  statusPending: { background: '#fff3e0', color: '#e65100', padding: '0.15rem 0.5rem', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600 },
  statusApproved: { background: '#e8f5e9', color: '#2e7d32', padding: '0.15rem 0.5rem', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600 },
  statusRejected: { background: '#fce4ec', color: '#c62828', padding: '0.15rem 0.5rem', borderRadius: 4, fontSize: '0.72rem', fontWeight: 600 },
  expand: { fontSize: '1.1rem', color: 'rgba(0,0,0,0.3)', fontWeight: 300, width: 20, textAlign: 'center' },
  cardBody: { padding: '0 1.25rem 1.25rem', borderTop: '1px solid #f0f0f0' },
  meta: { fontSize: '0.82rem', color: 'rgba(0,0,0,0.5)', margin: '0.75rem 0 0' },
  table: { width: '100%', borderCollapse: 'collapse', marginTop: '1rem' },
  th: { padding: '0.5rem 0.75rem', textAlign: 'left', fontSize: '0.72rem', fontWeight: 600, color: 'rgba(0,0,0,0.4)', textTransform: 'uppercase', borderBottom: '1px solid #e0e0e0' },
  td: { padding: '0.5rem 0.75rem', fontSize: '0.85rem', borderBottom: '1px solid #f5f5f5' },
  actions: { marginTop: '1.25rem', display: 'flex', gap: '1rem', flexWrap: 'wrap', alignItems: 'flex-start' },
  approveBtn: { background: '#2e7d32', color: '#fff', border: 'none', borderRadius: 6, padding: '0.6rem 1.5rem', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' },
  rejectGroup: { display: 'flex', gap: '0.5rem', flex: 1, minWidth: 200 },
  rejectInput: { flex: 1, border: '1px solid #ddd', borderRadius: 6, padding: '0.5rem 0.75rem', fontSize: '0.85rem', outline: 'none' },
  rejectBtn: { background: '#c62828', color: '#fff', border: 'none', borderRadius: 6, padding: '0.6rem 1.2rem', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' },
};
